/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import ApiRequest from '../../../../../api/ApiRequest'
import { API_AUTH_TYPES, API_ROUTES } from '../../../../../api'
import BlankModal from '../../../../modals/BlankModal'
import { FaRegSave } from "react-icons/fa";
import { ToolTipBottom, ToolTipHoverContainer } from '../../../../ToolTips'
import { SaveIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline'
import { LuBrain } from 'react-icons/lu'
import SmallLoadingSpinner from '../../../../loading/SmallLoadingSpinner'

const parabotApi = new ApiRequest(API_ROUTES.PARABOT, API_AUTH_TYPES.ADVISOR)

// Helper function to format category names for display
const formatCategory = (category) => {
    if (!category) return 'General'
    return category.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
}

// Helper function to get category color
const getCategoryColor = (category) => {
    const colorMap = {
        'general': 'bg-gray-100 text-gray-800',
        'preferences': 'bg-blue-100 text-blue-800',
        'mistakes': 'bg-red-100 text-red-800',
        'writing_style': 'bg-purple-100 text-purple-800',
        'task_patterns': 'bg-green-100 text-green-800',
        'context_awareness': 'bg-yellow-100 text-yellow-800',
        'client_insights': 'bg-indigo-100 text-indigo-800',
        'workflow': 'bg-pink-100 text-pink-800',
        'technical': 'bg-orange-100 text-orange-800'
    }
    return colorMap[category] || 'bg-gray-100 text-gray-800'
}

// Grouping options
const GROUP_OPTIONS = [
    { id: 'none', label: 'No Grouping' },
    { id: 'category', label: 'Group by Category' },
    { id: 'household', label: 'Group by Household' }
]

const Memory = () => {
    const queryClient = useQueryClient()
    const [open, setOpen] = useState(false)
    const [groupBy, setGroupBy] = useState('none')
    const [groupDropdownOpen, setGroupDropdownOpen] = useState(false)
    const [expandedGroups, setExpandedGroups] = useState({})
    const groupDropdownRef = React.useRef(null)

    const { data: memories, isLoading } = useQuery({
        queryKey: ['parabot-memories'],
        queryFn: parabotApi.getFn({
            endpoint: 'index_memories',
        })
    })

    const deleteMemory = useMutation(
        (id) => parabotApi.patch({
            endpoint: 'archive_memory',
            params: [id],
        }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('parabot-memories')
            }
        }
    )

    useEffect(() => {
        queryClient.invalidateQueries('parabot-memories')
    }, [open])

    // Handle clicks outside dropdown to close it
    useEffect(() => {
        function handleClickOutside(event) {
            if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
                setGroupDropdownOpen(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    // Reset expanded groups when grouping changes
    useEffect(() => {
        setExpandedGroups({})
    }, [groupBy])

    // Group memories based on selected grouping option
    const groupedMemories = React.useMemo(() => {
        if (!memories || groupBy === 'none') {
            return { 'All Memories': memories || [] }
        }

        return memories.reduce((groups, memory) => {
            let groupKey
            if (groupBy === 'category') {
                groupKey = formatCategory(memory.category)
            } else if (groupBy === 'household') {
                groupKey = memory.household_name || 'No Household'
            }

            if (!groups[groupKey]) {
                groups[groupKey] = []
            }
            groups[groupKey].push(memory)
            return groups
        }, {})
    }, [memories, groupBy])

    // Sort groups alphabetically, with special handling for "No Household"
    const sortedGroups = React.useMemo(() => {
        const entries = Object.entries(groupedMemories)
        if (groupBy === 'household') {
            return entries.sort(([a], [b]) => {
                if (a === 'No Household') return 1
                if (b === 'No Household') return -1
                return a.localeCompare(b)
            })
        }
        return entries.sort(([a], [b]) => a.localeCompare(b))
    }, [groupedMemories, groupBy])

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }))
    }

    const renderMemoryItem = (memory) => (
        <li key={memory.id} className='text-left text-sm text-gray-800 p-3'>
            <div className="flex items-start justify-between space-x-3">
                <div className="flex-1 min-w-0">
                    <p className="text-gray-900 mb-2">{memory.content}</p>
                    <div className="flex flex-wrap items-center gap-2">
                        {groupBy !== 'category' && (
                            <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(memory.category)}`}
                            >
                                {formatCategory(memory.category)}
                            </span>
                        )}
                        {groupBy !== 'household' && memory.household_name && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                🏠 {memory.household_name}
                            </span>
                        )}
                        <span className="text-xs text-gray-500">
                            {new Date(memory.created_at).toLocaleDateString()}
                        </span>
                    </div>
                </div>
                <button
                    onClick={() => { deleteMemory.mutate(memory.id) }}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                >
                    <TrashIcon className='h-4 w-4' />
                </button>
            </div>
        </li>
    )

    if (isLoading) return <div><SmallLoadingSpinner textColor='text-white' /></div>

    return (
        <div>
            <BlankModal open={open} setOpen={setOpen} >
                <div className="flex items-center justify-between mb-4">
                    <h3 className='text-left'>Parabot Memories</h3>

                    {/* Grouping Dropdown */}
                    <div className="relative" ref={groupDropdownRef}>
                        <button
                            className="px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200"
                            onClick={() => setGroupDropdownOpen(!groupDropdownOpen)}
                        >
                            <span>{GROUP_OPTIONS.find(opt => opt.id === groupBy)?.label}</span>
                            <ChevronDownIcon className="h-4 w-4" />
                        </button>

                        {groupDropdownOpen && (
                            <div className="absolute right-0 z-10 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                                <div className="py-1" role="menu">
                                    {GROUP_OPTIONS.map(option => (
                                        <button
                                            key={option.id}
                                            className={`block px-4 py-2 text-sm w-full text-left ${groupBy === option.id
                                                ? "bg-blue-50 text-blue-800"
                                                : "text-gray-700 hover:bg-gray-100"
                                                }`}
                                            onClick={() => {
                                                setGroupBy(option.id);
                                                setGroupDropdownOpen(false);
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <p className="text-left text-sm text-gray-600 mb-5">
                    Parabot will become more helpful as you chat, picking up on details and preferences to tailor its responses to you.
                    To understand what Parabot remembers or teach it something new, just chat with it:
                </p>

                <div className='border-gray-300 border rounded-lg max-h-[500px] overflow-y-auto custom-scrollbar-thick custom-scrollbar-gray'>
                    {groupBy === 'none' ? (
                        <ul className='divide-y divide-gray-300 py-2'>
                            {memories && memories.map(renderMemoryItem)}
                        </ul>
                    ) : (
                        <div className="divide-y divide-gray-300">
                            {sortedGroups.map(([groupName, groupMemories], groupIndex) => {
                                const isExpanded = expandedGroups[groupName] || false
                                return (
                                    <div key={groupName} className={groupIndex === 0 ? "py-2" : "pt-4 pb-2"}>
                                        <button
                                            onClick={() => toggleGroup(groupName)}
                                            className="w-full px-4 py-2 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                                    {groupBy === 'category' && (
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(groupMemories[0]?.category)}`}>
                                                            {groupName}
                                                        </span>
                                                    )}
                                                    {groupBy === 'household' && (
                                                        <>
                                                            {groupName === 'No Household' ? (
                                                                <span className="text-gray-600">📋 {groupName}</span>
                                                            ) : (
                                                                <span className="text-emerald-600">🏠 {groupName}</span>
                                                            )}
                                                        </>
                                                    )}
                                                    <span className="text-xs text-gray-500">({groupMemories.length})</span>
                                                </h4>
                                                {isExpanded ? (
                                                    <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                                                ) : (
                                                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                                )}
                                            </div>
                                        </button>
                                        {isExpanded && (
                                            <ul className='divide-y divide-gray-200'>
                                                {groupMemories.map(renderMemoryItem)}
                                            </ul>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </BlankModal>
            <ToolTipHoverContainer>
                <ToolTipBottom>
                    <p>Manage Parabot Memories</p>
                </ToolTipBottom>
                <button
                    onClick={() => setOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-2 flex items-center transition-all duration-200 transform hover:scale-105 shadow-md"
                >
                    <SaveIcon className='h-5 w-5 mr-2 text-white' />
                    <span>Memories</span>
                </button>
            </ToolTipHoverContainer>
        </div>
    )
}

export default Memory